import { StreamData } from 'types/streamData'
import { isRightToLeftText } from 'utils/rtlTextUtil'
import { getShortString } from 'utils/unicodeSubstringUtil'
import { Metadata } from '../../adaptor/metadata'
import originalAuthorResolver from '../originalAuthor/originalAuthorResolver'
import postTypeResolver from '../document/postTypeResolver'
import hitSentenceResolver from '../sentence/hitSentenceResolver'
import resolveCleanTextRelatedFields from '../cleanText/cleanTextUtil'
import resolveHighlightingRelatedFields from '../highlighting/highlightingUtil'
import { getDocumentTruncateIndex } from './documentTruncateIndexUtil'
import { resolveReplyToAuthors } from './replyToAuthorsUtil'
import { removeMetaData } from './retweetsUtil'
import { decorateDisplayText, decorateHighlightedDisplayText } from './decorateDisplayTextUtil'
import { isVoc } from '../util/isVocUtil'
import escapeHtml from '../util/escapeUtil'
import insertTagFromHighlightingMap from '../util/insertTagUtil'

const ELLIPSIS =
  ' <i class="fa fa-ellipsis-h" ' +
  'style="background-color: #999999; color: white; padding: 0 3px; ' +
  'border-radius: 3px;"></i> '

const resolveDisplayTextWhenNotVoc = (
  metadata: Metadata,
  isNeedTruncated: boolean | undefined,
  ellipsisIndices: number[],
  highlightedSentences?: string[],
  highlightingMaps?: { [key: number]: string }[]
) => {
  let isTruncatedText = false

  let truncatedObj
  const sentences = metadata.getSentences()
  let sentenceLength = sentences ? sentences.length : 0
  if (isNeedTruncated) {
    truncatedObj = getDocumentTruncateIndex(metadata)
    const index = truncatedObj.index!
    if (index !== sentenceLength) {
      sentenceLength = index + 1
    }
  }

  let displayText = ''
  let highlightedDisplayText = ''
  let cleanTruncateText = ''
  for (let index = 0; index < sentenceLength; index += 1) {
    const sentence = sentences![index]!.replace(/\u200B/g, '')

    let displaySentence = sentence
    let escapedDisplaySentence = escapeHtml(displaySentence)
    let cleanSentence = sentence
    let highlightedSentence

    if (highlightedSentences) {
      highlightedSentence = highlightedSentences[index]
    }
    if (truncatedObj && index === sentenceLength - 1 && truncatedObj.lastSentenceLimit > 0) {
      const lastLimit = truncatedObj.lastSentenceLimit
      displaySentence = getShortString(displaySentence, lastLimit, 200)
      escapedDisplaySentence = escapeHtml(displaySentence)
      cleanSentence = displaySentence
      isTruncatedText =
        sentenceLength !== sentences!.length || sentence.length > displaySentence.length

      // re-highlighted last truncated sentence
      if (highlightingMaps) {
        const map = highlightingMaps[index]
        if (map) {
          highlightedSentence = insertTagFromHighlightingMap(displaySentence, map)
        }
      }
    }

    const { hitSentence } = hitSentenceResolver(metadata)
    if (hitSentence && sentences![index] !== hitSentence) {
      highlightedSentence = `<span class="peer-sentence">${escapedDisplaySentence}</span>`
      displaySentence = `<span class="peer-sentence">${escapedDisplaySentence}</span>`
    } else {
      displaySentence = escapedDisplaySentence
    }

    displayText = displayText
      ? displayText + (ellipsisIndices.indexOf(index) > 0 ? ELLIPSIS : ' ') + displaySentence
      : displaySentence
    cleanTruncateText = cleanTruncateText ? `${cleanTruncateText} ${cleanSentence}` : cleanSentence

    if (highlightedSentence) {
      highlightedDisplayText = highlightedDisplayText
        ? highlightedDisplayText +
          (ellipsisIndices.indexOf(index) > 0 ? ELLIPSIS : ' ') +
          highlightedSentence
        : highlightedSentence
    }
  }

  return { isTruncatedText, cleanTruncateText, displayText, highlightedDisplayText }
}

function resolvePartialDisplayText(
  sentences: string[],
  highlightedSentences: string[],
  highlightingMaps: { [key: number]: string }[],
  metadata: Metadata,
  isNeedTruncated: boolean | undefined
) {
  const ellipsisIndices: number[] = []
  // copied from resolveDisplayTextWhenNotVoc
  let isTruncatedText = false

  let truncatedObj
  let sentenceLength = sentences ? sentences.length : 0
  if (isNeedTruncated) {
    truncatedObj = getDocumentTruncateIndex(metadata)
    const index = truncatedObj.index!
    if (index !== sentenceLength) {
      sentenceLength = index + 1
    }
  }

  let displayText = ''
  let highlightedDisplayText = ''
  let cleanTruncateText = ''

  for (let index = 0; index < sentenceLength; index += 1) {
    const sentence = (sentences![index] ?? '')?.replace(/\u200B/g, '')

    let displaySentence = sentence
    let escapedDisplaySentence = escapeHtml(displaySentence)
    let cleanSentence = sentence
    let highlightedSentence

    if (highlightedSentences) {
      highlightedSentence = highlightedSentences[index]
    }
    if (truncatedObj && index === sentenceLength - 1 && truncatedObj.lastSentenceLimit > 0) {
      const lastLimit = truncatedObj.lastSentenceLimit
      displaySentence = getShortString(displaySentence, lastLimit, 200)
      escapedDisplaySentence = escapeHtml(displaySentence)
      cleanSentence = displaySentence
      isTruncatedText =
        sentenceLength !== sentences!.length || sentence.length > displaySentence.length

      // re-highlighted last truncated sentence
      if (highlightingMaps) {
        const map = highlightingMaps[index]
        if (map) {
          highlightedSentence = insertTagFromHighlightingMap(displaySentence, map)
        }
      }
    }

    const { hitSentence } = hitSentenceResolver(metadata)
    if (hitSentence && sentences![index] !== hitSentence) {
      highlightedSentence = `<span class="peer-sentence">${escapedDisplaySentence}</span>`
      displaySentence = `<span class="peer-sentence">${escapedDisplaySentence}</span>`
    } else {
      displaySentence = escapedDisplaySentence
    }

    displayText = displayText
      ? displayText + (ellipsisIndices.indexOf(index) > 0 ? ELLIPSIS : ' ') + displaySentence
      : displaySentence
    cleanTruncateText = cleanTruncateText ? `${cleanTruncateText} ${cleanSentence}` : cleanSentence

    if (highlightedSentence) {
      highlightedDisplayText = highlightedDisplayText
        ? highlightedDisplayText +
          (ellipsisIndices.indexOf(index) > 0 ? ELLIPSIS : ' ') +
          highlightedSentence
        : highlightedSentence
    }
  }

  return { isTruncatedText, cleanTruncateText, displayText, highlightedDisplayText }
}

const resolvePodcastText = (metadata: Metadata, isNeedTruncated: boolean | undefined) => {
  const { highlightedSentences, highlightingMaps } = resolveHighlightingRelatedFields(metadata)
  const sentencesMetaTags = metadata.getSentenceMetaTags() ?? []
  const podcastDescriptionIndex = sentencesMetaTags.findIndex((tag) =>
    tag.includes('EPISODE DESCRIPTION')
  )
  const podcastTransciptIndex = sentencesMetaTags.findIndex((tag) =>
    tag.includes('EPISODE TRANSCRIPT')
  )

  const hasPodcastTranscript = podcastTransciptIndex >= 0

  // * podcast episode description sentences...
  const podcastTransciptSliceEndIndex = hasPodcastTranscript ? podcastTransciptIndex : undefined
  const podcastEpisodeDescriptionSentences = metadata
    .getSentences()
    ?.slice(0, podcastTransciptSliceEndIndex)
  const podcastEpisodeDescriptionHighlightedSentences = (highlightedSentences ?? []).slice(
    0,
    podcastTransciptSliceEndIndex
  )
  const podcastEpisodeDescriptionHighlightingMaps = (highlightingMaps ?? []).slice(
    0,
    podcastTransciptSliceEndIndex
  )

  // * podcast transcript sentences...
  const podcastTranscriptSentences = hasPodcastTranscript
    ? metadata.getSentences()?.slice(podcastTransciptIndex)
    : []
  const podcastTranscriptHighlightedSentences = hasPodcastTranscript
    ? (highlightedSentences ?? []).slice(podcastTransciptIndex)
    : []
  const podcastTranscriptHighlightingMaps = hasPodcastTranscript
    ? (highlightingMaps ?? []).slice(podcastTransciptIndex)
    : []

  const episodeDescriptionArtifact =
    podcastDescriptionIndex !== -1
      ? resolvePartialDisplayText(
          podcastEpisodeDescriptionSentences ?? [],
          podcastEpisodeDescriptionHighlightedSentences,
          podcastEpisodeDescriptionHighlightingMaps,
          metadata,
          isNeedTruncated
        )
      : {
          isTruncatedText: false,
          cleanTruncateText: '',
          displayText: '',
          highlightedDisplayText: ''
        }
  const transcriptArtifact =
    podcastTransciptIndex !== -1
      ? resolvePartialDisplayText(
          podcastTranscriptSentences ?? [],
          podcastTranscriptHighlightedSentences,
          podcastTranscriptHighlightingMaps,
          metadata,
          isNeedTruncated
        )
      : {
          isTruncatedText: false,
          cleanTruncateText: '',
          displayText: '',
          highlightedDisplayText: ''
        }
  return {
    episodeDescriptionArtifact,
    transcriptArtifact
  }
}

const resolveDisplayText = (metadata: Metadata, isNeedTruncated: boolean | undefined) => {
  const { highlightedSentences, highlightingMaps } = resolveHighlightingRelatedFields(metadata)
  let isTruncatedText = false
  let cleanTruncateText = ''
  let displayText = ''
  let highlightedDisplayText = ''

  let episodeDescriptionTruncated = false
  let episodeDescriptionDisplayText = ''
  let episodeDescriptionHighlightedDisplayText = ''

  let transcriptTruncated = false
  let transcriptDisplayText = ''
  let transcriptHighlightedDisplayText = ''

  if (!isVoc(metadata)) {
    const { ellipsisIndices } = resolveCleanTextRelatedFields(metadata)

    const artifact = resolveDisplayTextWhenNotVoc(
      metadata,
      isNeedTruncated,
      ellipsisIndices!,
      highlightedSentences,
      highlightingMaps
    )
    // * might add if stream data is podcast than resolve this
    const { episodeDescriptionArtifact, transcriptArtifact } = resolvePodcastText(
      metadata,
      isNeedTruncated
    )

    isTruncatedText = artifact.isTruncatedText
    cleanTruncateText = artifact.cleanTruncateText
    displayText = artifact.displayText
    highlightedDisplayText = artifact.highlightedDisplayText
    // * podcast related fields
    episodeDescriptionTruncated = episodeDescriptionArtifact.isTruncatedText
    episodeDescriptionDisplayText = episodeDescriptionArtifact.displayText ?? ''
    episodeDescriptionHighlightedDisplayText =
      episodeDescriptionArtifact.highlightedDisplayText ?? ''
    transcriptTruncated = transcriptArtifact.isTruncatedText
    transcriptDisplayText = transcriptArtifact.displayText ?? ''
    transcriptHighlightedDisplayText = transcriptArtifact.highlightedDisplayText ?? ''
  }

  const { originalAuthor } = originalAuthorResolver(metadata)
  const { postType } = postTypeResolver(metadata)

  return {
    isTruncatedText,
    isDisplayTextRtl: isRightToLeftText(
      removeMetaData(cleanTruncateText, originalAuthor!, postType!)
    ),
    displayText: decorateDisplayText(displayText, metadata),
    highlightedDisplayText:
      highlightedSentences !== undefined
        ? decorateHighlightedDisplayText(highlightedDisplayText, metadata)
        : '',
    replyToAuthors: resolveReplyToAuthors(displayText, metadata),
    // * podcast related fields
    descriptionDisplayText: decorateDisplayText(episodeDescriptionDisplayText, metadata),
    descriptionHighlightedDisplayText: decorateHighlightedDisplayText(
      episodeDescriptionHighlightedDisplayText,
      metadata
    ),
    isDescriptionTruncated: episodeDescriptionTruncated,
    isTranscriptTruncated: transcriptTruncated,
    transcriptDisplayText: decorateDisplayText(transcriptDisplayText, metadata),
    transcriptHighlightedDisplayText: decorateHighlightedDisplayText(
      transcriptHighlightedDisplayText,
      metadata
    )
  } as StreamData
}

const resolveTruncatedDisplayText = (metadata: Metadata) => resolveDisplayText(metadata, true)

const resolveUntruncatedDisplayText = (metadata: Metadata) => resolveDisplayText(metadata, false)

const resolveTruncatedAndUntruncatedDisplayText = (metadata: Metadata) => {
  const result = resolveTruncatedDisplayText(metadata)

  const { isTruncatedText } = result
  if (!isTruncatedText) {
    return result
  }

  const {
    displayText: untruncatedDisplayText,
    highlightedDisplayText: untruncatedHighlightedDisplayText,
    descriptionDisplayText: untruncatedDescriptionDisplayText,
    descriptionHighlightedDisplayText: untruncatedDescriptionHighlightedDisplayText,
    transcriptDisplayText: untruncatedTranscriptDisplayText,
    transcriptHighlightedDisplayText: untruncatedTranscriptHighlightedDisplayText
  } = resolveUntruncatedDisplayText(metadata)

  return {
    ...result,
    untruncatedDisplayText,
    untruncatedHighlightedDisplayText,
    untruncatedDescriptionDisplayText,
    untruncatedDescriptionHighlightedDisplayText,
    untruncatedTranscriptDisplayText,
    untruncatedTranscriptHighlightedDisplayText
  }
}

export const resolveDisplayTextRelatedFields = (
  callback: (artifact: StreamData) => any,
  metadata: Metadata
) => {
  const artifact = resolveTruncatedAndUntruncatedDisplayText(metadata)
  return callback(artifact)
}

export default resolveDisplayTextRelatedFields
